import { SortDefinition, SortOrderBy } from '@anecdotes/design-system/data-manipulation';
import { CompanyResponse, TrustCenterFacadeService, TrustCenterSharedModalsService } from '@anecdotes/shared-trust-center';
import { sortAlphabetically } from '@anecdotes/utils';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';

export interface ExtendedComapny extends CompanyResponse {
  userCount?: number;
}

@Component({
  selector: 'app-companies-renderer',
  templateUrl: './companies-renderer.component.html',
  styleUrls: ['./companies-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompaniesRendererComponent implements OnInit, OnChanges {
  readonly startOrderBy = SortOrderBy.DESC;
  readonly sortDefinitions: SortDefinition<ExtendedComapny>[] = [
    {
      id: 'name',
      translationKey: this.buildTranslationKey('company.name'),
      propertySelector: (company) => company.name,
      sortFunc: sortAlphabetically,
    },
    {
      id: 'access-status',
      translationKey: this.buildTranslationKey('company.accessStatus'),
      propertySelector: (company) => company.is_active,
    },
    {
      id: 'user-count',
      translationKey: this.buildTranslationKey('company.userCount'),
      propertySelector: (company) => company.userCount,
    },
    {
      id: 'created-source',
      translationKey: this.buildTranslationKey('company.ceatedSource'),
      propertySelector: (company) => company.created_source,
    },
    {
      id: 'creation-time',
      translationKey: this.buildTranslationKey('company.creationTime'),
      propertySelector: (company) => company.created_at,
    },
    {
      id: 'nda-status',
      translationKey: this.buildTranslationKey('company.ndaStatus'),
      propertySelector: (company) => company.nda_status,
    },
    {
      id: 'profile',
      translationKey: this.buildTranslationKey('company.permissionProfile'),
      propertySelector: (company) => company.permission_profile_id,
    },
    {
      id: 'auto-approval-actions',
      translationKey: this.buildTranslationKey('company.autoApproval'),
      propertySelector: (company) => company.automatic_approval,
    },
  ];

  @Input()
  allCompanies: CompanyResponse[];

  sortedData$ = new BehaviorSubject<ExtendedComapny[]>([]);
  hasCompanies$: Observable<boolean>;
  extenededCompanies$: Observable<ExtendedComapny[]>;

  constructor(
    private readonly trustCenterFacadeService: TrustCenterFacadeService,
    private readonly trustCenterSharedModalsService: TrustCenterSharedModalsService,
  ) {}

  ngOnInit(): void {
    this.hasCompanies$ = this.trustCenterFacadeService.getCompanies().pipe(map((companies) => !!companies.length));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.allCompanies) {
      const streams: Observable<ExtendedComapny>[] = this.allCompanies.map((company: CompanyResponse) => {
        return this.trustCenterFacadeService.getCompanyUserCount(company.id).pipe(
          map((userCount: number) => {
            return {
              ...company,
              userCount,
            } as ExtendedComapny;
          }),
        );
      });
  
      this.extenededCompanies$ = streams.length ? combineLatest(streams) : of([]);
    }
  }

  idSelector(item: ExtendedComapny): string {
    return item.id?.toString();
  }

  buildTranslationKey(key: string): string {
    return `trustCenter.${key}`;
  }

  handleSort(sorted: ExtendedComapny[]): void {
    this.sortedData$.next(sorted);
  }

  openAddCompanyModal(): void {
    this.trustCenterSharedModalsService.openAddEditCompanySidePanel();
  }
}
